import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_TRANSACTION_KPI } from 'api/graphql/queries/transactionKpiQuery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCoins, 
  faHandHoldingDollar, 
  faMoneyBillTransfer,
  faPercent,
  faMoneyCheckDollar
} from '@fortawesome/free-solid-svg-icons';

interface TransactionTotals {
  total_sales: number;
  total_earnings: number;
  total_commission: number;
  total_withdrawal: number;
  total_payment: number;
}

const TransactionMetricsDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Handler to navigate to transactions page
  const handleNavigateToTransactions = () => {
    navigate('/transactions');
  };
  const [transactionTotals, setTransactionTotals] = useState<TransactionTotals>({
    total_sales: 0,
    total_earnings: 0,
    total_commission: 0,
    total_withdrawal: 0,
    total_payment: 0
  });
  
  const [animateCounters, setAnimateCounters] = useState(false);
  
  // Using all-time data (not filtering by days)
  const { loading, error, data } = useQuery(GET_TRANSACTION_KPI, {
    fetchPolicy: 'network-only'
  });
  
  useEffect(() => {
    if (data?.getTransactionKpi?.transaction_totals) {
      setTransactionTotals(data.getTransactionKpi.transaction_totals);
      setTimeout(() => setAnimateCounters(true), 300);
    }
  }, [data]);
  
  // Format number as currency - shorter version
  const formatCurrency = (amount: number) => {
    // For large numbers, format in K or M
    if (amount >= 1000000) {
      return (amount / 1000000).toFixed(1) + 'M ' + t('transactionSummary.dinarSymbol');
    } else if (amount >= 1000) {
      return (amount / 1000).toFixed(1) + 'K ' + t('transactionSummary.dinarSymbol');
    } else {
      return amount.toFixed(0) + ' ' + t('transactionSummary.dinarSymbol');
    }
  };

  // Calculate Commission Rate as a percentage
  const commissionRate = transactionTotals.total_sales > 0 
    ? ((transactionTotals.total_commission / transactionTotals.total_sales) * 100).toFixed(1) 
    : "0.0";
  
  // Counter animation
  const AnimatedCounter: React.FC<{value: number, format: (n: number) => string}> = ({ value, format }) => {
    const [displayValue, setDisplayValue] = useState(0);
    
    useEffect(() => {
      if (animateCounters && value > 0) {
        const duration = 2000; // 2 seconds
        const framesPerSecond = 60;
        const totalFrames = duration / 1000 * framesPerSecond;
        const increment = value / totalFrames;
        
        let frame = 0;
        const timer = setInterval(() => {
          frame++;
          setDisplayValue(prev => {
            const newValue = prev + increment;
            if (newValue >= value || frame >= totalFrames) {
              clearInterval(timer);
              return value;
            }
            return newValue;
          });
        }, 1000 / framesPerSecond);
        
        return () => clearInterval(timer);
      } else {
        setDisplayValue(value);
      }
    }, [value, animateCounters]);
    
    return <span>{format(displayValue)}</span>;
  };
  
  // Common style for all metric boxes
  const metricBoxStyle = {
    height: '74px', // Increased height
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem', // Increased padding
    borderRadius: '0.75rem', // Increased border radius
    border: '1px solid rgba(0,0,0,0.1)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.03)' // Added subtle shadow
  };
  
  // Common style for metric icons
  const iconBoxStyle = {
    width: '28px', 
    height: '28px', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.375rem',
    marginRight: '0.5rem'
  };
  
  return (
    <Card 
      className="h-100 shadow-sm border-0 overflow-visible cursor-pointer"
      style={{
        background: 'var(--phoenix-card-bg, linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%))',
        borderRadius: '0.75rem'
      }}
      onClick={handleNavigateToTransactions}
    >
      <Card.Header className="p-3 d-flex flex-column justify-content-center">
      <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0 fw-bold text-body">
            <FontAwesomeIcon icon={faMoneyCheckDollar} className="me-2" />
            {t('transactionMetrics.title')}
          </h5>
          <small className="text-primary">
            {t('viewDetails')} →
          </small>
        </div>
      </Card.Header>
      <Card.Body className="pt-0 pb-4 px-3 d-flex flex-column justify-content-center">
        
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-grow text-secondary" style={{ width: '1.5rem', height: '1.5rem' }} role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            <div className="col-6">
              <div 
                style={{
                  ...metricBoxStyle,
                  background: 'rgba(220,53,69,0.05)'
                }}
              >
                <div style={{
                  ...iconBoxStyle,
                  background: 'rgba(220,53,69,0.1)'
                }}>
                  <FontAwesomeIcon icon={faPercent} size="xs" className="text-danger" />
                </div>
                <div style={{ width: 'calc(100% - 36px)' }} 
                     title={`${t('commissionRate')}: ${commissionRate}%`}>
                  <div className="text-danger fw-semibold mb-1" style={{ fontSize: '0.7rem' }}>{t('commissionRate')}</div>
                  <div className="fs-7 fw-bold text-danger" style={{ fontSize: '0.9rem' }}>{commissionRate}%</div>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div 
                style={{
                  ...metricBoxStyle,
                  background: 'rgba(13,202,240,0.05)'
                }}
              >
                <div style={{
                  ...iconBoxStyle,
                  background: 'rgba(13,202,240,0.1)'
                }}>
                  <FontAwesomeIcon icon={faHandHoldingDollar} size="xs" className="text-info" />
                </div>
                <div style={{ width: 'calc(100% - 36px)' }}
                     title={`${t('totalPayments')}: ${transactionTotals.total_payment.toLocaleString('fr-TN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${t('transactionSummary.dinarSymbol')}`}>
                  <div className="text-info fw-semibold mb-1" style={{ fontSize: '0.7rem' }}>{t('totalPayments')}</div>
                  <div className="fs-7 fw-bold text-info text-truncate" style={{ fontSize: '0.9rem' }}>
                    <AnimatedCounter value={transactionTotals.total_payment} format={formatCurrency} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div 
                style={{
                  ...metricBoxStyle,
                  background: 'rgba(255,193,7,0.05)'
                }}
              >
                <div style={{
                  ...iconBoxStyle,
                  background: 'rgba(255,193,7,0.1)'
                }}>
                  <FontAwesomeIcon icon={faMoneyBillTransfer} size="xs" className="text-warning" />
                </div>
                <div style={{ width: 'calc(100% - 36px)' }}
                     title={`${t('withdrawals')}: ${transactionTotals.total_withdrawal.toLocaleString('fr-TN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${t('transactionSummary.dinarSymbol')}`}>
                  <div className="text-warning fw-semibold mb-1" style={{ fontSize: '0.7rem' }}>{t('withdrawals')}</div>
                  <div className="fs-7 fw-bold text-warning text-truncate" style={{ fontSize: '0.9rem' }}>
                    <AnimatedCounter value={transactionTotals.total_withdrawal} format={formatCurrency} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div 
                style={{
                  ...metricBoxStyle,
                  background: 'rgba(108,117,125,0.05)'
                }}
              >
                <div style={{
                  ...iconBoxStyle,
                  background: 'rgba(108,117,125,0.1)'
                }}>
                  <FontAwesomeIcon icon={faCoins} size="xs" className="text-body" />
                </div>
                <div style={{ width: 'calc(100% - 36px)' }}
                     title={`${t('totalCommission')}: ${transactionTotals.total_commission.toLocaleString('fr-TN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${t('transactionSummary.dinarSymbol')}`}>
                  <div className="text-body fw-semibold mb-1" style={{ fontSize: '0.7rem' }}>{t('totalCommission')}</div>
                  <div className="fs-7 fw-bold text-body text-truncate" style={{ fontSize: '0.9rem' }}>
                    <AnimatedCounter value={transactionTotals.total_commission} format={formatCurrency} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default TransactionMetricsDetail;
